/**
 * TeamView — per-workspace memberships + invitations.
 *
 * S-062 / FEAT-006 (Phase 4). Replaces the AdminStubs.TeamView placeholder.
 * Admin-only screen reachable from Settings → Team.
 *
 * What it does:
 *   - Workspace selector — defaults to the first agencyclient in the
 *     tenant. Memberships are per-agencyclient; invitations are
 *     tenant-wide (FEAT-108 / S-436) and do not change with it.
 *   - Members section — GET /memberships?agencyclient_id=N. Each row
 *     shows the teammate's email (enriched server-side from Clerk),
 *     name if available, role chip, joined date, and inline actions:
 *       • Role select — PATCH /memberships/:id (admin only)
 *       • Remove     — DELETE /memberships/:id with a confirm modal
 *     The current user's own row is rendered with the Remove action
 *     disabled to prevent accidental self-revoke.
 *   - Pending invitations section — GET /invitations, one list for the
 *     tenant. Each row: email, role, workspaces, created date, expires
 *     date, Revoke action.
 *   - Invite modal — POST /invitations with email, role and the ticked
 *     workspaces (at least one, none locked; the first ticked is where the
 *     invitee lands). Surfaces 409 inline ("Already invited or a member").
 *     Client-side validates email format and length via the same caps as
 *     the server (RFC 5321 max 254).
 *
 * Role enum: matches the DB CHECK constraint and the invitations route
 * schema: `admin | editor | reviewer`. The instructions referenced
 * "viewer" but the platform uses `reviewer` end-to-end (db/migrations/
 * 0001_rls_and_roles.sql line 25; apps/api/src/routes/invitations.ts
 * line 38). Using the actual enum to avoid 422 round-trips.
 *
 * Clerk Free-tier note: invitations beyond 5 will fail with a 402-ish
 * code from Clerk (the test instance hot-raccoon-76 is on the free
 * plan). The mutation surfaces whatever error Clerk returns; the user
 * can resolve by upgrading the Clerk plan.
 */

import { useEffect, useMemo, useState } from "react";
// Consolidation (option 3): shared admin modal shell. Replaces the local
// ModalShell that used to live at the bottom of this file. (This view has no
// drawer, so it imports ModalShell only.)
import { ModalShell } from "@/components/SettingsModalShell";
import { useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { apiErrorPayload } from "@/lib/api-error";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { UserPicker } from "@/components/UserPicker";
import { SectionHeader } from "@/components/SectionHeader";
import {
  canRemoveMember,
  canChangeRole,
  isInvitationExpired,
  type Guard,
} from "@/lib/member-guards";
import { toggleInOrder, workspaceNames } from "@/lib/invite-workspaces";

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

type MembershipRole = "admin" | "editor" | "reviewer";

interface MembershipRow {
  id: number;
  agencyclient_id: number;
  clerk_user_id: string;
  user_email: string | null;
  user_name: string | null;
  role: MembershipRole;
  created_at: string;
}

interface MembershipsResponse {
  data: MembershipRow[];
  meta: { total: number; limit: number; offset: number };
}

interface InvitationRow {
  id: string;
  email: string;
  role: string; // "org:admin" | "org:editor" | "org:reviewer" — Clerk-prefixed
  status: string;
  agencyclient_ids: number[];
  created_at: string;
  expires_at: string;
}

interface InvitationsResponse {
  data: InvitationRow[];
  meta: { total: number };
}

const ROLE_LABELS: Record<MembershipRole, string> = {
  admin: "Admin",
  editor: "Editor",
  reviewer: "Reviewer",
};

const ROLE_HELP: Record<MembershipRole, string> = {
  admin: "Full access — can manage members, settings, and content.",
  editor: "Can create, edit, and run pieces; cannot manage members.",
  reviewer: "Read + comment only; cannot publish.",
};

/**
 * S-081 — surface the server-side last-admin orphan guard cleanly.
 *
 * The /memberships DELETE and PATCH endpoints return 409 with
 *   { "error": "would_orphan",
 *     "message": "Cannot remove the last admin from this workspace…" }
 * when a remove or demote would leave the workspace with zero admins.
 *
 * ApiError keeps the raw response text in `body`. Without this helper
 * the user would see the JSON blob rendered as-is. We detect the
 * sentinel `"error": "would_orphan"` shape and pull out the friendly
 * `message`; everything else falls through to the existing
 * `mutation.error.message` rendering.
 */
function orphanGuardMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  if (!err.body) return null;
  try {
    const parsed = JSON.parse(err.body) as {
      error?: string;
      message?: string;
    };
    if (parsed.error === "would_orphan" && typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // Body wasn't JSON — fall through.
  }
  return null;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// Strip the Clerk "org:" prefix so the chip reads "Admin" not "org:admin".
function unprefix(role: string): MembershipRole {
  const bare = role.startsWith("org:") ? role.slice(4) : role;
  if (bare === "admin" || bare === "editor" || bare === "reviewer") {
    return bare;
  }
  return "reviewer";
}

function isValidEmail(s: string): boolean {
  // Same shape Clerk and our zod schema use. Keep it lenient — the
  // canonical check happens server-side.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && s.length <= 254;
}

export function TeamView() {
  const { user } = useUser();
  const currentClerkUserId = user?.id ?? null;

  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useApiQuery<Agencyclient[]>(["agencyclients"], "/agencyclients");

  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Default to the first workspace once the list loads.
  useEffect(() => {
    if (selectedId == null && workspaces && workspaces.length > 0) {
      setSelectedId(workspaces[0].id);
    }
  }, [workspaces, selectedId]);

  if (wsLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading team…</div>
        </div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {wsError instanceof ApiError && wsError.status === 403
              ? "You don't have permission to view team settings."
              : (wsError.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <SectionHeader
            eyebrow="Settings · Team"
            title="Team"
            description="Manage memberships per workspace and invite teammates."
          />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings → Workspaces before inviting
              teammates.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <TeamViewBody
      workspaces={workspaces}
      selectedId={selectedId}
      onSelect={setSelectedId}
      currentClerkUserId={currentClerkUserId}
    />
  );
}

interface TeamBodyProps {
  workspaces: Agencyclient[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  currentClerkUserId: string | null;
}

function TeamViewBody({
  workspaces,
  selectedId,
  onSelect,
  currentClerkUserId,
}: TeamBodyProps) {
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  // Phase 5 UX polish (2026-05-25): pendingId reflects the workspace
  // the user is switching TO during the activate() round-trip; the
  // local `selectedId` only flips after activation succeeds. This
  // eliminates the empty-state flash that occurred when the
  // memberships query refetched under the OLD JWT.
  const [pendingId, setPendingId] = useState<number | null>(null);
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? workspaces[0],
    [workspaces, selectedId]
  );
  const activeId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  useEnsureActiveWorkspace(activeId);

  // S-077 — wire the selector to the active-workspace switcher so
  // invitations and membership updates land under the right JWT scope.
  // Without this, Invite Teammate would 500 against any workspace
  // other than the one stamped in the JWT at onboarding.
  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["memberships", activeId] });
    void qc.invalidateQueries({ queryKey: ["invitations"] });
  }

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeId) return;
    clearError();
    // Phase 5 UX polish (2026-05-25): hold local selection until
    // activate() resolves to avoid the empty-state flash. See
    // PublicationsView.handleWorkspaceChange for the full rationale.
    setPendingId(nextId);
    try {
      await activate(nextId);
      onSelect(nextId);
    } catch {
      // useActiveWorkspace surfaces the error via switchError.
    } finally {
      setPendingId(null);
    }
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Team"
          title="Team"
          description="Per-workspace memberships and pending invitations."
          action={
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowInvite(true)}
              disabled={isActivating}
            >
              + Invite teammate
            </button>
          }
        />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingId ?? activeId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && (
            <span className="team-toolbar-hint">
              Switching workspace…
            </span>
          )}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
        </div>

        <MembersSection
          agencyclientId={activeId}
          workspaceName={activeWs.name}
          currentClerkUserId={currentClerkUserId}
          onChanged={invalidate}
        />

        <InvitationsSection
          agencyclientId={activeId}
          workspaces={workspaces}
          onChanged={invalidate}
        />
      </div>

      {showInvite && (
        <InviteModal
          agencyclientId={activeId}
          workspaces={workspaces}
          onClose={() => setShowInvite(false)}
          onSent={() => {
            invalidate();
            setShowInvite(false);
          }}
        />
      )}
    </main>
  );
}

// ---------- Members ----------

interface MembersProps {
  agencyclientId: number;
  workspaceName: string;
  currentClerkUserId: string | null;
  onChanged: () => void;
}

function MembersSection({
  agencyclientId,
  workspaceName,
  currentClerkUserId,
  onChanged,
}: MembersProps) {
  const { data, isLoading, error } = useApiQuery<MembershipsResponse>(
    ["memberships", agencyclientId],
    `/memberships?agencyclient_id=${agencyclientId}`
  );

  const [confirmRemove, setConfirmRemove] = useState<MembershipRow | null>(
    null
  );
  const [showAddExisting, setShowAddExisting] = useState(false);

  return (
    <section className="team-section">
      <div className="settings-subheader">
        <h2>Members</h2>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setShowAddExisting(true)}
        >
          + Add existing user
        </button>
      </div>

      {isLoading && <div className="state-loading">Loading members…</div>}

      {error && !isLoading && (
        <div className="state-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view members."
            : (error.message ?? "Could not load members.")}
        </div>
      )}

      {!isLoading && !error && (data?.data?.length ?? 0) === 0 && (
        <div className="team-section-empty">
          No members yet. Invite a teammate above.
        </div>
      )}

      {!isLoading && !error && (data?.data?.length ?? 0) > 0 && (
        <table className="team-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Joined</th>
              <th className="team-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data!.data.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                removeGuard={canRemoveMember(m, data!.data, currentClerkUserId)}
                roleGuard={canChangeRole(m, data!.data, currentClerkUserId)}
                isSelf={
                  currentClerkUserId != null &&
                  m.clerk_user_id === currentClerkUserId
                }
                onRoleChanged={onChanged}
                onRequestRemove={() => setConfirmRemove(m)}
              />
            ))}
          </tbody>
        </table>
      )}

      {confirmRemove && (
        <ConfirmRemoveModal
          member={confirmRemove}
          workspaceName={workspaceName}
          onCancel={() => setConfirmRemove(null)}
          onRemoved={() => {
            onChanged();
            setConfirmRemove(null);
          }}
        />
      )}

      {showAddExisting && (
        <AddExistingUserModal
          agencyclientId={agencyclientId}
          workspaceName={workspaceName}
          onClose={() => setShowAddExisting(false)}
          onAdded={() => {
            onChanged();
            setShowAddExisting(false);
          }}
        />
      )}
    </section>
  );
}

// ---------- Add existing user (S-178) ----------

interface AddExistingProps {
  agencyclientId: number;
  workspaceName: string;
  onClose: () => void;
  onAdded: () => void;
}

function AddExistingUserModal({
  agencyclientId,
  workspaceName,
  onClose,
  onAdded,
}: AddExistingProps) {
  const api = useApi();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<MembershipRole>("editor");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      // One POST /memberships per selected user. Sequential keeps error
      // attribution simple; the counts are small (tenant teammates).
      for (const clerkUserId of selected) {
        await api("/memberships", {
          method: "POST",
          body: JSON.stringify({
            clerk_user_id: clerkUserId,
            agencyclient_id: agencyclientId,
            role,
          }),
        });
      }
      onAdded();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.status === 409
            ? "One or more users are already members of this workspace."
            : (e.message ?? "Could not add user(s).")
          : "Could not add user(s)."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Team" title={`Add to ${workspaceName}`}>
      <div className="settings-form">
        <p className="settings-confirm-copy">
          Add existing teammates to <strong>{workspaceName}</strong> without
          sending an invitation email. They get access immediately.
        </p>

        <UserPicker
          excludeAgencyclientId={agencyclientId}
          selectedIds={selected}
          onToggle={toggle}
          emptyLabel="Everyone in your tenant is already in this workspace."
        />

        <label className="settings-field">
          <span className="settings-label">Role for added users</span>
          <select
            className="settings-select"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
          >
            <option value="admin">{ROLE_LABELS.admin}</option>
            <option value="editor">{ROLE_LABELS.editor}</option>
            <option value="reviewer">{ROLE_LABELS.reviewer}</option>
          </select>
          <span
            className="settings-label-hint"
            style={{ marginTop: 4, fontSize: 12 }}
          >
            {ROLE_HELP[role]}
          </span>
        </label>

        {err && <div className="settings-form-error">{err}</div>}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void submit()}
            disabled={busy || selected.size === 0}
          >
            {busy
              ? "Adding…"
              : `Add ${selected.size || ""} ${
                  selected.size === 1 ? "user" : "users"
                }`.trim()}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

interface MemberRowProps {
  member: MembershipRow;
  isSelf: boolean;
  /** Why Remove is unavailable, when it is — S-374's visible last-admin guard. */
  removeGuard: Guard;
  roleGuard: Guard;
  onRoleChanged: () => void;
  onRequestRemove: () => void;
}

function MemberRow({
  member,
  isSelf,
  removeGuard,
  roleGuard,
  onRoleChanged,
  onRequestRemove,
}: MemberRowProps) {
  const mutation = useApiMutation<MembershipRow, { role: MembershipRole }>(
    `/memberships/${member.id}`,
    "PATCH",
    {
      onSuccess: () => onRoleChanged(),
    }
  );

  function handleRoleChange(next: MembershipRole) {
    if (next === member.role) return;
    mutation.mutate({ role: next });
  }

  return (
    <tr>
      <td>
        <div className="team-member-name">
          {member.user_name || member.user_email || "(unknown user)"}
          {isSelf && (
            <span
              className="pill pill-info"
              style={{ marginLeft: 8, verticalAlign: "middle" }}
            >
              You
            </span>
          )}
        </div>
        {member.user_email && member.user_name && (
          <div className="team-member-email">{member.user_email}</div>
        )}
      </td>
      <td>
        <select
          className="settings-select"
          value={member.role}
          disabled={mutation.isPending || !roleGuard.allowed}
          title={roleGuard.allowed ? ROLE_HELP[member.role] : undefined}
          onChange={(e) =>
            handleRoleChange(e.target.value as MembershipRole)
          }
        >
          <option value="admin">{ROLE_LABELS.admin}</option>
          <option value="editor">{ROLE_LABELS.editor}</option>
          <option value="reviewer">{ROLE_LABELS.reviewer}</option>
        </select>
        {/* House rule: a disabled control says why, in text beside it — not
            in a title only the hesitant hover. */}
        {!roleGuard.allowed && (
          <div className="settings-guard-note">{roleGuard.reason}</div>
        )}
        {mutation.error && (
          <div className="settings-field-error" style={{ marginTop: 4 }}>
            {orphanGuardMessage(mutation.error) ?? mutation.error.message}
          </div>
        )}
      </td>
      <td className="ws-date">{fmtDate(member.created_at)}</td>
      <td className="team-actions-col">
        <div className="team-actions">
          <button
            type="button"
            className="btn-ghost btn-danger"
            onClick={onRequestRemove}
            disabled={!removeGuard.allowed}
            title={removeGuard.allowed ? "Remove from this workspace" : undefined}
          >
            Remove
          </button>
        </div>
        {!removeGuard.allowed && (
          <div className="settings-guard-note">{removeGuard.reason}</div>
        )}
      </td>
    </tr>
  );
}

interface ConfirmRemoveProps {
  member: MembershipRow;
  workspaceName: string;
  onCancel: () => void;
  onRemoved: () => void;
}

function ConfirmRemoveModal({
  member,
  workspaceName,
  onCancel,
  onRemoved,
}: ConfirmRemoveProps) {
  const mutation = useApiMutation<void, void>(
    `/memberships/${member.id}`,
    "DELETE",
    {
      onSuccess: () => onRemoved(),
    }
  );

  const who = member.user_email || member.user_name || "this user";

  return (
    <ModalShell onClose={onCancel} eyebrow="Team · danger" title="Remove member?">
      <p className="settings-confirm-copy">
        Remove <strong>{who}</strong> from <strong>{workspaceName}</strong>?
        They will lose access immediately. Their Clerk account is not deleted.
      </p>
      {mutation.error && (
        <div className="settings-form-error">
          {orphanGuardMessage(mutation.error) ?? mutation.error.message}
        </div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Removing…" : "Remove member"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- Invitations ----------

interface InvitationsProps {
  /** The selected workspace — only a fallback for resending an old invitation. */
  agencyclientId: number;
  workspaces: Agencyclient[];
  onChanged: () => void;
}

// S-436 — one list for the tenant, the same whichever workspace is selected.
function InvitationsSection({ agencyclientId, workspaces, onChanged }: InvitationsProps) {
  const { data, isLoading, error } = useApiQuery<InvitationsResponse>(
    ["invitations"],
    "/invitations"
  );

  return (
    <section className="team-section">
      <div className="settings-subheader">
        <h2>Pending invitations</h2>
      </div>

      {isLoading && (
        <div className="state-loading">Loading invitations…</div>
      )}

      {error && !isLoading && (
        <div className="state-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view invitations."
            : (error.message ?? "Could not load invitations.")}
        </div>
      )}

      {!isLoading && !error && (data?.data?.length ?? 0) === 0 && (
        <div className="team-section-empty">No pending invitations.</div>
      )}

      {!isLoading && !error && (data?.data?.length ?? 0) > 0 && (
        <table className="team-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Workspaces</th>
              <th>Invited</th>
              <th>Expires</th>
              <th className="team-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data!.data.map((inv) => (
              <InvitationRowView
                key={inv.id}
                invitation={inv}
                agencyclientId={agencyclientId}
                workspaces={workspaces}
                onRevoked={onChanged}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface InvRowProps {
  invitation: InvitationRow;
  agencyclientId: number;
  workspaces: Agencyclient[];
  onRevoked: () => void;
}

function InvitationRowView({
  invitation,
  agencyclientId,
  workspaces,
  onRevoked,
}: InvRowProps) {
  const mutation = useApiMutation<void, void>(
    `/invitations/${invitation.id}`,
    "DELETE",
    {
      onSuccess: () => onRevoked(),
    }
  );
  const roleLabel = ROLE_LABELS[unprefix(invitation.role)];
  const expired = isInvitationExpired(invitation.expires_at);

  // S-374 — there is no resend endpoint; an expired invitation is revoked
  // and re-created. Delete first, because Clerk refuses a second pending
  // invitation for the same address. If the re-create then fails the old row
  // is already gone, so the error says so plainly rather than leaving the
  // reader to guess whether anything still stands.
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const api = useApi();

  async function resend() {
    setResending(true);
    setResendError(null);
    try {
      await api(`/invitations/${invitation.id}`, {
        method: "DELETE",
      });
      try {
        // Re-sent to the same workspaces, in the same order. An invitation
        // too old to record any goes to the selected workspace.
        const ids =
          invitation.agencyclient_ids.length > 0
            ? invitation.agencyclient_ids
            : [agencyclientId];
        await api("/invitations", {
          method: "POST",
          body: JSON.stringify({
            email: invitation.email,
            role: unprefix(invitation.role),
            agencyclient_ids: ids,
          }),
        });
      } catch (err) {
        setResendError(
          `The expired invitation was removed, but the new one could not be sent (${
            err instanceof Error ? err.message : "unknown error"
          }). Invite ${invitation.email} again.`
        );
      }
      onRevoked();
    } catch (err) {
      setResendError(
        err instanceof Error ? err.message : "Could not resend the invitation."
      );
    } finally {
      setResending(false);
    }
  }

  return (
    <tr className={expired ? "is-expired" : ""}>
      <td>{invitation.email}</td>
      <td>
        <span className="pill pill-muted">{roleLabel}</span>
      </td>
      <td>{workspaceNames(invitation.agencyclient_ids, workspaces).join(", ") || "—"}</td>
      <td className="ws-date">{fmtDate(invitation.created_at)}</td>
      <td className="ws-date">
        {fmtDate(invitation.expires_at)}
        {expired && <span className="pill pill-warn inv-expired-pill">Expired</span>}
      </td>
      <td className="team-actions-col">
        <div className="team-actions">
          {expired ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => void resend()}
              disabled={resending}
            >
              {resending ? "Resending…" : "Resend"}
            </button>
          ) : (
            <button
              type="button"
              className="btn-ghost btn-danger"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Revoking…" : "Revoke"}
            </button>
          )}
        </div>
        {resendError && (
          <div className="settings-field-error" style={{ marginTop: 4, textAlign: "right" }}>
            {resendError}
          </div>
        )}
        {mutation.error && (
          <div
            className="settings-field-error"
            style={{ marginTop: 4, textAlign: "right" }}
          >
            {mutation.error.message}
          </div>
        )}
      </td>
    </tr>
  );
}

// ---------- Invite modal ----------

interface InviteProps {
  /** The selected workspace — ticked to start with, and can be unticked. */
  agencyclientId: number;
  workspaces: Agencyclient[];
  onClose: () => void;
  onSent: () => void;
}

interface InviteBody {
  email: string;
  role: MembershipRole;
  agencyclient_ids: number[];
}

interface InviteResponse {
  invitation_id: string;
  email: string;
  role: string;
  agencyclient_ids: number[];
}

function InviteModal({
  agencyclientId,
  workspaces,
  onClose,
  onSent,
}: InviteProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MembershipRole>("editor");
  // S-436 — the ticked workspaces, in the order they were ticked: the first
  // is where the invitee lands. The selected workspace starts ticked, like
  // any other box it can be unticked; at least one must stay.
  const [selectedIds, setSelectedIds] = useState<number[]>([agencyclientId]);
  const landingName = workspaceNames(selectedIds.slice(0, 1), workspaces)[0];

  const mutation = useApiMutation<InviteResponse, InviteBody>(
    "/invitations",
    "POST",
    {
      onSuccess: () => onSent(),
    }
  );

  const emailError = email.length > 0 && !isValidEmail(email);
  // 409 is also `team_full` (the Clerk org's member limit), which is not
  // about this email — only `already_invited` belongs under the field.
  const inviteError = apiErrorPayload(mutation.error);
  const alreadyInvited = inviteError.error === "already_invited";
  const canSubmit =
    !mutation.isPending && isValidEmail(email) && selectedIds.length > 0;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate({
      email: email.trim(),
      role,
      agencyclient_ids: selectedIds,
    });
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Team" title="Invite teammate">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Email</span>
          <input
            className="settings-input"
            type="email"
            value={email}
            maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            placeholder="teammate@example.com"
            spellCheck={false}
            autoComplete="off"
          />
          {emailError && (
            <span className="settings-field-error">
              Enter a valid email address.
            </span>
          )}
          {alreadyInvited && (
            <span className="settings-field-error">
              Already invited or a member. Revoke the
              existing invitation first if you want to re-send.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Role</span>
          <select
            className="settings-select"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
          >
            <option value="admin">{ROLE_LABELS.admin}</option>
            <option value="editor">{ROLE_LABELS.editor}</option>
            <option value="reviewer">{ROLE_LABELS.reviewer}</option>
          </select>
          <span
            className="settings-label-hint"
            style={{ marginTop: 4, fontSize: 12 }}
          >
            {ROLE_HELP[role]}
          </span>
        </label>

        {workspaces.length > 1 && (
          <div className="settings-field">
            <span className="settings-label">Workspaces</span>
            <span
              className="settings-label-hint"
              style={{ marginBottom: 6, fontSize: 12 }}
            >
              Tick every workspace they should join (one role applies to
              all).{" "}
              {landingName ? (
                <>
                  They will land in <strong>{landingName}</strong>, the first
                  one ticked.
                </>
              ) : (
                "Tick at least one."
              )}
            </span>
            <ul className="user-picker-list">
              {workspaces.map((w) => (
                <li key={w.id} className="user-picker-item">
                  <label className="user-picker-row">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(w.id)}
                      onChange={() => setSelectedIds((prev) => toggleInOrder(prev, w.id))}
                    />
                    <span className="user-picker-meta">
                      <span className="user-picker-name">{w.name}</span>
                      <span className="user-picker-email">{w.slug}</span>
                      {selectedIds[0] === w.id && (
                        <span className="user-picker-count">lands here</span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {mutation.error && !alreadyInvited && (
          <div className="settings-form-error">
            {inviteError.message ?? (mutation.error.body || mutation.error.message)}
          </div>
        )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// NOTE: the local ModalShell / DrawerShell that used to live here were removed
// in the option-3 consolidation — both now come from
// @/components/SettingsModalShell (imported at the top of this file). The shared
// shell renders the canonical anatomy (eyebrow + 24px serif title, bordered
// close, blurred overlay) and handles Escape/backdrop close.
